import { useState } from "react";
import { AlertCircle, ArrowDown, ArrowUp, GripVertical, Plus, Trash2 } from "lucide-react";
import { CUSTOMERS } from "@/lib/customers";
import { newCustomer, newQuestion, questionIssues, move } from "@/lib/quizFactory";
import { CustomerAvatar } from "@/components/admin/AdminHeader";

const IconBtn = ({ onClick, title, children, testId }) => (
  <button data-testid={testId} title={title} onClick={(e) => { e.stopPropagation(); onClick(); }}
    className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">{children}</button>
);

export const BuilderSidebar = ({ quiz, sel, setSel, update, t }) => {
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);

  const addCustomer = () => {
    const c = quiz.customers.length;
    update((qz) => qz.customers.push(newCustomer(c, 1)));
    setSel({ c, q: null });
  };
  const addQuestion = (ci) => {
    const q = quiz.customers[ci].questions.length;
    update((qz) => qz.customers[ci].questions.push(newQuestion()));
    setSel({ c: ci, q });
  };
  const moveCustomer = (ci, dir) => { update((qz) => move(qz.customers, ci, dir)); setSel({ c: Math.max(0, Math.min(quiz.customers.length - 1, ci + dir)), q: null }); };
  const deleteCustomer = (ci) => {
    if (!window.confirm(`${t("delete_customer")}?`)) return;
    update((qz) => qz.customers.splice(ci, 1));
    setSel({ c: null, q: null });
  };

  // Drag a question to a new position (qi) in customer ci; qi null => append.
  const moveQuestion = (from, ci, qi) => {
    setDrag(null); setOver(null);
    if (!from) return;
    if (from.ci === ci && (qi === from.qi || qi === from.qi + 1)) return;
    const origLen = quiz.customers[ci].questions.length;
    let idx = qi == null ? origLen : qi;
    if (from.ci === ci && from.qi < idx) idx -= 1;
    update((qz) => {
      const [q] = qz.customers[from.ci].questions.splice(from.qi, 1);
      qz.customers[ci].questions.splice(idx, 0, q);
    });
    setSel({ c: ci, q: idx });
  };

  return (
    <aside className="w-72 shrink-0 border-r border-border bg-card/50 overflow-y-auto" data-testid="builder-sidebar">
      <div className="p-4 flex flex-col gap-3">
        {quiz.customers.map((c, ci) => (
          <div key={c.id} className={`rounded-lg border bg-background/60 transition-colors ${over === `c-${ci}` ? "border-primary ring-1 ring-primary" : "border-border"}`}>
            <div
              data-testid={`sidebar-customer-${ci + 1}`}
              onClick={() => setSel({ c: ci, q: null })}
              onDragOver={(e) => { if (drag) { e.preventDefault(); setOver(`c-${ci}`); } }}
              onDrop={(e) => { if (drag) { e.preventDefault(); moveQuestion(drag, ci, null); } }}
              className={`flex items-center gap-2.5 p-2.5 cursor-pointer rounded-t-lg transition-colors ${sel.c === ci && sel.q === null ? "bg-primary/15" : "hover:bg-secondary/60"}`}
            >
              <CustomerAvatar src={CUSTOMERS[c.type].portrait} className="w-9 h-9" />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">{t("customer")} {ci + 1}</div>
                <div className="text-sm font-semibold truncate">{c.name || "—"}</div>
              </div>
              <IconBtn title={t("move_up")} onClick={() => moveCustomer(ci, -1)} testId={`customer-move-up-${ci + 1}`}><ArrowUp className="w-3.5 h-3.5" /></IconBtn>
              <IconBtn title={t("move_down")} onClick={() => moveCustomer(ci, 1)} testId={`customer-move-down-${ci + 1}`}><ArrowDown className="w-3.5 h-3.5" /></IconBtn>
              <IconBtn title={t("delete_customer")} onClick={() => deleteCustomer(ci)} testId={`customer-delete-${ci + 1}`}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
            </div>
            <div
              className="px-2 pb-2 flex flex-col gap-0.5 min-h-[0.5rem]"
              onDragOver={(e) => { if (drag) { e.preventDefault(); setOver(`c-${ci}`); } }}
              onDrop={(e) => { if (drag) { e.preventDefault(); moveQuestion(drag, ci, null); } }}
            >
              {c.questions.map((q, qi) => {
                const bad = questionIssues(q).length > 0;
                const dragging = drag && drag.ci === ci && drag.qi === qi;
                return (
                  <button
                    key={q.id}
                    data-testid={`sidebar-question-${ci + 1}-${qi + 1}`}
                    draggable
                    onDragStart={(e) => { e.stopPropagation(); setDrag({ ci, qi }); }}
                    onDragEnd={() => { setDrag(null); setOver(null); }}
                    onDragOver={(e) => { if (drag) { e.preventDefault(); setOver(`q-${ci}-${qi}`); } }}
                    onDrop={(e) => { if (drag) { e.preventDefault(); e.stopPropagation(); moveQuestion(drag, ci, qi); } }}
                    onClick={() => setSel({ c: ci, q: qi })}
                    className={`relative flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded transition-colors ${over === `q-${ci}-${qi}` ? "before:content-[''] before:absolute before:-top-[3px] before:left-1 before:right-1 before:h-[3px] before:rounded-full before:bg-primary before:shadow-[0_0_8px_2px_rgba(99,102,241,0.7)]" : ""} ${dragging ? "opacity-40" : ""} ${sel.c === ci && sel.q === qi ? "bg-primary text-primary-foreground" : "hover:bg-secondary/60"}`}
                  >
                    <GripVertical className="w-3.5 h-3.5 opacity-40 shrink-0 cursor-grab" />
                    <span className="font-mono-jb text-xs opacity-70">Q{qi + 1}</span>
                    <span className="truncate flex-1">{q.text || "…"}</span>
                    {bad && <AlertCircle className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                  </button>
                );
              })}
              <button data-testid={`sidebar-add-question-${ci + 1}`} onClick={() => addQuestion(ci)} className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary px-2 py-1.5">
                <Plus className="w-3.5 h-3.5" />{t("add_question")}
              </button>
            </div>
          </div>
        ))}
        <button data-testid="sidebar-add-customer" onClick={addCustomer} className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border py-3 text-sm font-semibold text-muted-foreground hover:text-primary hover:border-primary transition-colors">
          <Plus className="w-4 h-4" />{t("add_customer")}
        </button>
      </div>
    </aside>
  );
};
